import React from 'react';
import type { FC } from 'react';
import { match } from 'variant';
import { Box, Text, Alert, Spinner, useAsyncCallback } from '@wix/cli-ui-kit';
import { useDevCenterOverridesClient } from '@wix/dev-center-overrides-client';
import { useDebugLog } from '@wix/cli-debug-log';
import { useMetasiteManagerClient } from '@wix/metasite-manager-client';
import { useErrorReporter } from '@wix/cli-error-reporting';
import { useSiteConfig } from '../../model.js';
import { CreatePreview } from './Steps/CreatePreview.js';

interface ReleaseProps {
  baseUrl?: string;
  label?: string;
  comment?: string;
  createMinorVersion: boolean;
}

export const ReleaseSite: FC<ReleaseProps> = ({
  baseUrl,
  label,
  createMinorVersion,
  comment,
}) => {
  const { appId, siteId } = useSiteConfig();
  const writeToDebugLog = useDebugLog();

  const { release } = useDevCenterOverridesClient();
  const metasiteManagerClient = useMetasiteManagerClient({ siteId });

  const errorReporter = useErrorReporter();

  const releaseAction = useAsyncCallback(async (_, tag: string) => {
    const baseUrl = await release(appId, tag, {
      createMinorVersion,
      comment,
    });

    await metasiteManagerClient
      .updateExternalSiteUrl(baseUrl)
      .catch((error: unknown) => {
        errorReporter.reportError(error);
        writeToDebugLog(error);
      });

    return { baseUrl };
  });

  function onPreviewCreated({ previewId }: { previewId: string }) {
    return releaseAction.execute(previewId);
  }

  return (
    <>
      <CreatePreview
        baseUrl={baseUrl}
        label={label}
        onCreated={onPreviewCreated}
      />
      {match(releaseAction.status, {
        NotRequested: () => null,
        Error: () => null,
        Loading: () => <Spinner text={<Text>Releasing...</Text>} />,
        Success: ({ result: { baseUrl } }) => (
          <Box flexDirection="column" gap={1}>
            <Alert type="success">
              <Text bold>Site published on {baseUrl}</Text>
            </Alert>
          </Box>
        ),
      })}
    </>
  );
};
